import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Table,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
  Pagination,
  PaginationItem,
  PaginationLink,
} from 'reactstrap';
import { diaryService } from '../services';
import { CalendarEvent, EventType } from '../types';
import BreadCrumb from '../components/Common/BreadCrumb';
import { toast } from 'react-toastify';
import {
  extractEventTimeForInput,
  formatEventTime,
  getBrowserTimezone,
} from '../utils/eventTimezone';
import { formatUkDate, formatUkDateLong } from '../utils/dateFormat';

const ITEMS_PER_PAGE = 20;

const CalendarEventsPage: React.FC = () => {
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState({
    eventTypeId: '',
    date: new Date().toISOString().slice(0, 10),
    startTime: '18:00',
    endTime: '19:00',
    title: '',
    shortDesc: '',
    description: '',
    webLink: '',
  });
  const [joinedModal, setJoinedModal] = useState<{ show: boolean; eventId: string; eventTitle?: string }>({ show: false, eventId: '', eventTitle: '' });
  const [joinedUsers, setJoinedUsers] = useState<any[]>([]);
  const [loadingJoined, setLoadingJoined] = useState(false);
  const [detailModal, setDetailModal] = useState<{ show: boolean; event: CalendarEvent | null }>({ show: false, event: null });
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; eventId: string }>({ show: false, eventId: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadEventTypes();
    loadEvents();
  }, []);

  const loadEventTypes = async () => {
    try {
      const data = await diaryService.getEventTypes();
      setEventTypes(Array.isArray(data) ? data : []);
    } catch {
      setEventTypes([]);
    }
  };

  const loadEvents = async () => {
    setLoading(true);
    try {
      const data = await diaryService.getCalendarEvents();
      setEvents(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const selected = eventTypes.find((et) => et._id === form.eventTypeId);
    if (!selected) {
      toast.error('Please select an event type');
      return;
    }
    setSaving(true);
    try {
      const startTime = editingEventId ? form.startTime.trim() : form.startTime.trim() || undefined;
      const endTime = editingEventId ? form.endTime.trim() : form.endTime.trim() || undefined;
      const payload = {
        eventType: selected.name,
        date: form.date,
        startTime,
        endTime,
        timezone:
          editingEventId || startTime || endTime
            ? getBrowserTimezone()
            : undefined,
        title: form.title.trim() || undefined,
        shortDesc: form.shortDesc.trim() || undefined,
        description: form.description.trim() || undefined,
        webLink: form.webLink.trim() || undefined,
        color: selected.color,
      };
      if (editingEventId) {
        await diaryService.updateCalendarEvent(editingEventId, payload);
        toast.success('Calendar event updated');
        handleCancelEdit();
      } else {
        await diaryService.createCalendarEvent(payload);
        toast.success('Calendar event added');
        setForm({
          eventTypeId: '',
          date: new Date().toISOString().slice(0, 10),
          startTime: '18:00',
          endTime: '19:00',
          title: '',
          shortDesc: '',
          description: '',
          webLink: '',
        });
      }
      loadEvents();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleViewDetail = async (ev: CalendarEvent) => {
    setDetailModal({ show: true, event: null });
    setLoadingDetail(true);
    try {
      const data = await diaryService.getCalendarEvent(ev._id);
      setDetailModal({ show: true, event: data });
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load event');
      setDetailModal({ show: false, event: null });
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleEdit = (ev: CalendarEvent) => {
    const startTimeStr = ev.startAt ? extractEventTimeForInput(ev.startAt) : '';
    const endTimeStr = ev.endAt ? extractEventTimeForInput(ev.endAt) : '';
    const dateStr = (ev.date ? new Date(ev.date) : new Date()).toISOString().slice(0, 10);
    const matched = eventTypes.find((et) => et.name === ev.eventType);
    setForm({
      eventTypeId: matched ? matched._id : eventTypes[0]?._id || '',
      date: dateStr,
      startTime: startTimeStr,
      endTime: endTimeStr,
      title: ev.title || '',
      shortDesc: ev.shortDesc || '',
      description: ev.description || '',
      webLink: ev.webLink || '',
    });
    setEditingEventId(ev._id);
    setDetailModal({ show: false, event: null });
  };

  const handleCancelEdit = () => {
    setEditingEventId(null);
    setForm({
      eventTypeId: '',
      date: new Date().toISOString().slice(0, 10),
      startTime: '18:00',
      endTime: '19:00',
      title: '',
      shortDesc: '',
      description: '',
      webLink: '',
    });
  };

  const handleViewJoined = async (ev: CalendarEvent) => {
    setJoinedModal({ show: true, eventId: ev._id, eventTitle: ev.title });
    setLoadingJoined(true);
    try {
      const data = await diaryService.getEventJoinedUsers(ev._id);
      setJoinedUsers(data.users || []);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load joined users');
      setJoinedUsers([]);
    } finally {
      setLoadingJoined(false);
    }
  };

  const openDeleteModal = (id: string) => {
    setDeleteModal({ show: true, eventId: id });
  };

  const handleDelete = async () => {
    if (!deleteModal.eventId) return;
    setDeleteId(deleteModal.eventId);
    try {
      await diaryService.deleteCalendarEvent(deleteModal.eventId);
      toast.success('Event deleted');
      loadEvents();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    } finally {
      setDeleteId(null);
      setDeleteModal({ show: false, eventId: '' });
    }
  };

  const formatDate = (d: string) => {
    try {
      return formatUkDateLong(d);
    } catch {
      return d;
    }
  };

  const getEventLabel = (eventType: string) => {
    const et = eventTypes.find((e) => e.name === eventType);
    if (et) return et.name;
    return eventType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const sortedEvents = useMemo(
    () =>
      [...events].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      ),
    [events],
  );

  const filteredEvents = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedEvents;
    return sortedEvents.filter((ev) => {
      const haystack = [
        getEventLabel(ev.eventType),
        ev.eventType,
        ev.title,
        ev.shortDesc,
        ev.description,
        formatDate(ev.date),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [sortedEvents, searchTerm, eventTypes]);

  const totalPages = Math.max(1, Math.ceil(filteredEvents.length / ITEMS_PER_PAGE));

  const paginatedEvents = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredEvents.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredEvents, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const getVisiblePages = (): (number | 'ellipsis')[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const show = new Set<number>([1, totalPages]);
    for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
      show.add(i);
    }
    const sorted = Array.from(show).sort((a, b) => a - b);
    const out: (number | 'ellipsis')[] = [];
    sorted.forEach((p, i) => {
      if (i > 0 && p - sorted[i - 1] > 1) out.push('ellipsis');
      out.push(p);
    });
    return out;
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb pageTitle="Calendar Events" />
          <Row>
            <Col lg={4}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title mb-0">
                      <i className="ri-calendar-event-line me-2"></i>
                      {editingEventId ? 'Edit Event' : 'Add Event'}
                    </h5>
                    {editingEventId && (
                      <Button color="light" size="sm" onClick={handleCancelEdit}>
                        Cancel
                      </Button>
                    )}
                  </div>
                  <p className="text-muted small mb-4">
                    {editingEventId ? 'Update the event details below.' : 'Select an event type (configured in Event Types).'}
                  </p>
                  <Form onSubmit={handleAdd}>
                    <FormGroup>
                      <Label>Event Type <span className="text-danger">*</span></Label>
                      <Input
                        type="select"
                        value={form.eventTypeId}
                        onChange={(e) => setForm((f) => ({ ...f, eventTypeId: e.target.value }))}
                        required
                      >
                        <option value="">Select event type...</option>
                        {eventTypes.map((et) => (
                          <option key={et._id} value={et._id}>
                            {et.name}
                          </option>
                        ))}
                      </Input>
                      {eventTypes.length === 0 && (
                        <small className="text-muted">Add event types in Diary → Event Types first.</small>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <Label>Date <span className="text-danger">*</span></Label>
                      <Input
                        type="date"
                        value={form.date}
                        onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                        required
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Start Time</Label>
                      <Input
                        type="time"
                        value={form.startTime}
                        onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))}
                      />
                      <small className="text-muted">When event starts (users can join before this)</small>
                    </FormGroup>
                    <FormGroup>
                      <Label>End Time</Label>
                      <Input
                        type="time"
                        value={form.endTime}
                        onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Title (optional)</Label>
                      <Input
                        type="text"
                        placeholder="e.g. Full Moon March 2024"
                        value={form.title}
                        onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Short Description</Label>
                      <Input
                        type="text"
                        placeholder="Brief summary"
                        value={form.shortDesc}
                        onChange={(e) => setForm((f) => ({ ...f, shortDesc: e.target.value }))}
                        maxLength={500}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Description</Label>
                      <Input
                        type="textarea"
                        rows={3}
                        placeholder="Full event description"
                        value={form.description}
                        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Web Link (optional)</Label>
                      <Input
                        type="url"
                        placeholder="https://example.com"
                        value={form.webLink}
                        onChange={(e) => setForm((f) => ({ ...f, webLink: e.target.value }))}
                      />
                    </FormGroup>
                    <Button type="submit" color="primary" disabled={saving}>
                      {saving ? <Spinner size="sm" className="me-2" /> : null}
                      <i className={`${editingEventId ? 'ri-save-line' : 'ri-add-line'} me-1`}></i>
                      {editingEventId ? 'Update Event' : 'Add Event'}
                    </Button>
                  </Form>
                </CardBody>
              </Card>
            </Col>
            <Col lg={8}>
              <Card>
                <CardBody>
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
                    <h5 className="card-title mb-0">
                      <i className="ri-list-check me-2"></i>
                      All Events
                      {!loading && events.length > 0 && (
                        <Badge color="light" className="ms-2 text-dark">
                          {filteredEvents.length}
                          {searchTerm.trim() ? ` of ${events.length}` : ''}
                        </Badge>
                      )}
                    </h5>
                    <Input
                      type="search"
                      placeholder="Search events..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="form-control"
                      style={{ maxWidth: 280 }}
                    />
                  </div>
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : events.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="ri-calendar-line display-4"></i>
                      <p className="mt-2">No events yet</p>
                    </div>
                  ) : filteredEvents.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="ri-search-line display-4"></i>
                      <p className="mt-2">No events match your search</p>
                    </div>
                  ) : (
                    <>
                    <Table hover responsive>
                          <thead>
                        <tr>
                          <th>Type</th>
                          <th>Date & Time</th>
                          <th>Title</th>
                          <th>Short Desc</th>
                          <th>Joined</th>
                          <th style={{ width: 200 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedEvents.map((ev) => (
                            <tr key={ev._id}>
                              <td>
                                <span
                                  className="rounded me-2"
                                  style={{
                                    display: 'inline-block',
                                    width: 10,
                                    height: 10,
                                    backgroundColor: ev.color || '#9CA3AF',
                                  }}
                                />
                                {getEventLabel(ev.eventType)}
                              </td>
                              <td>
                                {formatDate(ev.date)}
                                {(ev.startAt || ev.endAt) && (
                                  <small className="d-block text-muted">
                                    {ev.startAt && formatEventTime(ev.startAt)}
                                    {ev.startAt && ev.endAt && ' – '}
                                    {ev.endAt && formatEventTime(ev.endAt)}
                                  </small>
                                )}
                              </td>
                              <td>{ev.title || '-'}</td>
                              <td className="text-truncate" style={{ maxWidth: 120 }} title={ev.shortDesc}>
                                {ev.shortDesc || '-'}
                              </td>
                              <td>
                                <Button
                                  color="secondary"
                                  size="sm"
                                  outline
                                  onClick={() => handleViewJoined(ev)}
                                >
                                  <i className="ri-user-add-line me-1"></i>
                                  {ev.joinedCount ?? 0}
                                </Button>
                              </td>
                              <td>
                                <div className="d-flex gap-1">
                                  <Button
                                    color="primary"
                                    size="sm"
                                    outline
                                    onClick={() => handleViewDetail(ev)}
                                    title="View"
                                  >
                                    <i className="ri-eye-line"></i>
                                  </Button>
                                  <Button
                                    color="info"
                                    size="sm"
                                    outline
                                    onClick={() => handleEdit(ev)}
                                    title="Edit"
                                  >
                                    <i className="ri-edit-line"></i>
                                  </Button>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    outline
                                    disabled={deleteId === ev._id}
                                    onClick={() => openDeleteModal(ev._id)}
                                    title="Delete"
                                  >
                                    {deleteId === ev._id ? (
                                      <Spinner size="sm" />
                                    ) : (
                                      <i className="ri-delete-bin-line"></i>
                                    )}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                    <div className="pagination-bar">
                      <div className="text-muted">
                        Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
                        {Math.min(currentPage * ITEMS_PER_PAGE, filteredEvents.length)} of{' '}
                        {filteredEvents.length} events
                      </div>
                      {totalPages > 1 && (
                        <Pagination className="pagination-rounded mb-0">
                          <PaginationItem disabled={currentPage === 1}>
                            <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
                          </PaginationItem>
                          {getVisiblePages().map((page, idx) =>
                            page === 'ellipsis' ? (
                              <PaginationItem disabled key={`ellipsis-${idx}`}>
                                <PaginationLink>…</PaginationLink>
                              </PaginationItem>
                            ) : (
                              <PaginationItem active={page === currentPage} key={page}>
                                <PaginationLink onClick={() => handlePageChange(page)}>{page}</PaginationLink>
                              </PaginationItem>
                            ),
                          )}
                          <PaginationItem disabled={currentPage === totalPages}>
                            <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
                          </PaginationItem>
                        </Pagination>
                      )}
                    </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Event Detail Modal */}
      <Modal isOpen={detailModal.show} toggle={() => setDetailModal({ show: false, event: null })} size="lg">
        <ModalHeader toggle={() => setDetailModal({ show: false, event: null })}>
          <i className="ri-calendar-event-line me-2"></i>
          Event Details
        </ModalHeader>
        <ModalBody>
          {loadingDetail ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : detailModal.event ? (
            <div>
              <div className="d-flex align-items-start gap-2 mb-3">
                <span
                  className="rounded"
                  style={{
                    display: 'inline-block',
                    width: 16,
                    height: 16,
                    backgroundColor: detailModal.event.color || '#9CA3AF',
                  }}
                />
                <div>
                  <h5 className="mb-1">{detailModal.event.title || getEventLabel(detailModal.event.eventType)}</h5>
                  <Badge color="secondary">{getEventLabel(detailModal.event.eventType)}</Badge>
                  {detailModal.event.joinedCount !== undefined && (
                    <Badge color="info" className="ms-2">
                      {detailModal.event.joinedCount} joined
                    </Badge>
                  )}
                </div>
              </div>
              {detailModal.event.shortDesc && (
                <p className="text-muted mb-2"><strong>Short Description:</strong> {detailModal.event.shortDesc}</p>
              )}
              {detailModal.event.description && (
                <div className="mb-3">
                  <strong>Description:</strong>
                  <p className="mb-0 mt-1" style={{ whiteSpace: 'pre-wrap' }}>{detailModal.event.description}</p>
                </div>
              )}
              {detailModal.event.webLink && (
                <div className="mb-3">
                  <strong>Web Link:</strong>{' '}
                  <a href={detailModal.event.webLink} target="_blank" rel="noopener noreferrer">
                    {detailModal.event.webLink}
                  </a>
                </div>
              )}
              <div className="d-flex flex-wrap gap-3 text-muted small">
                <span><i className="ri-calendar-line me-1"></i> {formatDate(detailModal.event.date)}</span>
                {detailModal.event.startAt && (
                  <span><i className="ri-time-line me-1"></i> Start: {formatEventTime(detailModal.event.startAt)}</span>
                )}
                {detailModal.event.endAt && (
                  <span>End: {formatEventTime(detailModal.event.endAt)}</span>
                )}
              </div>
              <div className="d-flex gap-2 mt-4">
                <Button color="primary" outline onClick={() => handleEdit(detailModal.event!)}>
                  <i className="ri-edit-line me-1"></i> Edit
                </Button>
                <Button color="secondary" outline onClick={() => handleViewJoined(detailModal.event!)}>
                  <i className="ri-user-line me-1"></i> View Joined Users
                </Button>
              </div>
            </div>
          ) : null}
        </ModalBody>
      </Modal>

      {/* Joined Users Modal */}
      <Modal isOpen={joinedModal.show} toggle={() => setJoinedModal({ ...joinedModal, show: false })} size="md">
        <ModalHeader toggle={() => setJoinedModal({ ...joinedModal, show: false })}>
          <i className="ri-user-line me-2"></i>
          Joined Users
          {joinedModal.eventTitle && (
            <Badge color="info" className="ms-2">{joinedModal.eventTitle}</Badge>
          )}
        </ModalHeader>
        <ModalBody>
          {loadingJoined ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : joinedUsers.length === 0 ? (
            <p className="text-muted text-center mb-0">No users have joined yet.</p>
          ) : (
            <Table size="sm">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {joinedUsers.map((u) => (
                  <tr key={u._id}>
                    <td>{u.name || '-'}</td>
                    <td>{u.email || '-'}</td>
                    <td>{u.joinedAt ? formatUkDate(u.joinedAt) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={deleteModal.show} toggle={() => setDeleteModal({ show: false, eventId: '' })} centered>
        <ModalHeader toggle={() => setDeleteModal({ show: false, eventId: '' })}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          <p className="mb-3">Delete this calendar event?</p>
          <div className="d-flex justify-content-end gap-2">
            <Button
              type="button"
              color="light"
              onClick={() => setDeleteModal({ show: false, eventId: '' })}
              disabled={Boolean(deleteId)}
            >
              Cancel
            </Button>
            <Button type="button" color="danger" onClick={handleDelete} disabled={Boolean(deleteId)}>
              {deleteId ? <Spinner size="sm" className="me-2" /> : null}
              Delete
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default CalendarEventsPage;
